import { html, property, queryAssignedNodes } from 'lit-element';
import { nothing } from 'lit-html';
import { KatLitElement, register } from '../../shared/base';
import baseStyles from '../../shared/base/base.lit.scss';
import wrapperStyles from './panel-wrapper.lit.scss';
import { KatPanel } from './panel';

/**
 * @component {kat-panel-wrapper} KatalPanelWrapper A wrapper for a kat-panel. Controls how the panel interacts with its side-by-side content. The panel is a content container that exists outside the main page structure, like a modal. It provides a space for contextual information and/or actions that would otherwise clutter the page if included in the main body of the page. The panel will remain active until the user explicitly chooses to dismiss it by clicking the close button or completing an action.
 * @subcomponent ./panel.ts
 * @status Production
 * @slot default Main body content.
 * @slot panel Panel content.
 * @example Panel {
 *    "script": "
 *      const openPanelBtn = document.querySelector('#open-panel');
 *      const closePanelBtn = document.querySelector('#close-panel');
 *      const panel = document.querySelector('kat-panel');
 *
 *      openPanelBtn.addEventListener('click', () => panel.open());
 *      closePanelBtn.addEventListener('click', () => panel.close());
 *    ",
 *    "content": "
 *      <kat-button id='open-panel'>Open Panel</kat-button>
 *      <kat-button id='close-panel'>Close Panel</kat-button>
 *      <p>Wrapped content. Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed doeiusmod tempor incididunt ut labore et dolore magna aliqua.</p>
 *      <kat-panel slot=\"panel\" header=\"Panel header\">
 *        <p>Main body content. Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed doeiusmod tempor incididunt ut labore et dolore magna aliqua.</p>
 *      </kat-panel>
 *    "}
 * @a11y {keyboard}
 * @a11y {sr}
 * @a11y {contrast}
 */
@register('kat-panel-wrapper')
export class KatPanelWrapper extends KatLitElement {
  static get styles() {
    return [baseStyles, wrapperStyles];
  }

  /**
   * Indicates visibility of the slotted kat-panel(s).
   * When false, all kat-panel(s) are hidden.
   * When true and there is a single panel, open it.
   * When true and there are multiple panels, does nothing. Instead, rely on the `visible` property on the panel itself.
   * Defaults to false.
   */
  @property({ attribute: 'panel-visible' })
  panelVisible?: boolean = false;

  /**
   * The behavior of how the slotted kat-panel interacts with the content. Defaults to overlay.
   * @enum {value} overlay Panel will display over the main body content.
   * @enum {value} slide Panel will push main body content out of the way.
   */
  @property()
  variant?: 'slide' | 'overlay' = 'overlay';

  /** The size of the panel. Defaults to 'small'.
   * @enum {value} small Panel will inhabit a small portion of the screen.
   * @enum {value} large Panel will inhabit a large portion of the screen.
   */
  @property()
  size?: 'small' | 'large' = 'small';

  /** Controls whether the background mask is visible.  Only available when variant is overlay. Defaults to false. */
  @property({ attribute: 'mask-background' })
  maskBackground?: boolean = false;

  /** Slotted KatPanels. */
  @queryAssignedNodes('panel', true, 'kat-panel')
  panelElements: KatPanel[];

  constructor() {
    super();

    this._listeners = [
      ['close', this._handlePanelEvent],
      ['open', this._handlePanelEvent],
    ];
  }

  private _handlePanelEvent({ detail }) {
    const previouslyOpenPanels = this.panelElements.filter(panel =>
      panel.hasAttribute('visible')
    );
    // If only one panel was previously open, and we see a close event, then panelVisible = false
    if (previouslyOpenPanels.length <= 1 && !detail.visible) {
      this.panelVisible = false;
      // otherwise, at least one panel must be visible
    } else {
      this.panelVisible = true;
    }
  }

  // On initial render, if any panel is visible, set panelVisible to true.
  firstUpdated() {
    super.firstUpdated();

    // Ignore if consumer initialized with panel-visible already set to true
    if (!this.panelVisible) {
      this.panelVisible = this.panelElements.some(panel =>
        panel.hasAttribute('visible')
      );
    }
  }

  updated(changedProperties: Map<string, any>) {
    if (changedProperties.has('panelVisible')) {
      if (this.panelElements.length === 1) {
        this.panelElements[0].visible = this.panelVisible;
      } else if (this.panelElements.length > 1 && this.panelVisible === false) {
        this.panelElements.forEach(
          panel => (panel.visible = this.panelVisible)
        );
      }
    }
  }

  //TODO: Should auto-slot panel into the panel slot.
  render() {
    const renderedMaskBackground =
      this.maskBackground && this.variant === 'overlay'
        ? html`<div class="mask-background"></div>`
        : nothing;

    return html`
      <div class="content">
        <slot></slot>
        ${renderedMaskBackground}
      </div>
      <slot name="panel"></slot>
    `;
  }
}
